import React, { useCallback } from "react";
import { Slider } from "@/components/ui/slider";
import { Field, NumberInput, PanelSection } from "./controls";
import { CornerDownRight } from "lucide-react";

// Shared corner-radius controls for rect, image, and polygon shapes.
//
// Data model (identical to ImageControls so rect/image stay in sync):
//   cornerRadius        - shared radius value (used when not individual)
//   cornerRadiusUnit    - "px" | "%"
//   cornerIndividual    - boolean, when true each corner uses its own value
//   cornerTL/TR/BR/BL   - per-corner radius values (independent mode)
//
// Rendering:
//   rect / image -> roundedRectPath() with per-corner arcs (see ElementRenderer)
//   polygon      -> roundedPolygonPath() with tangent fillets (no independent
//                   mode — a polygon's corners aren't axis-aligned, so "TL/TR"
//                   doesn't apply; only the shared radius is used)
const CORNER_KEYS = ["cornerTL", "cornerTR", "cornerBR", "cornerBL"];
const CORNER_LABELS = { cornerTL: "左上", cornerTR: "右上", cornerBR: "右下", cornerBL: "左下" };

export default function CornerRadiusControls({ attrs, setAttr, type }) {
  const cornerUnit = attrs.cornerRadiusUnit === "%" ? "%" : "px";
  const cornerValue = Number(attrs.cornerRadius) || 0;
  // Polygons only support a shared radius (no per-corner independent mode).
  const individual = type !== "polygon" && !!attrs.cornerIndividual;
  // For % mode the meaningful range is 0–50 (0 = square, 50 = full pill).
  const cornerMax = cornerUnit === "%" ? 50 : 200;

  // Switch corner-radius unit. Uses the patch-object form of setAttr so both
  // fields are merged in a single update — two sequential setAttr calls would
  // clobber each other because each rebuilds attrs from the same stale snapshot.
  const setCornerUnit = useCallback(
    (unit) => {
      const max = unit === "%" ? 50 : 200;
      const patch = { cornerRadiusUnit: unit, cornerRadius: Math.min(cornerValue, max) };
      // Also clamp the per-corner values when switching units.
      CORNER_KEYS.forEach((k) => {
        const v = Number(attrs[k]) || 0;
        patch[k] = Math.min(v, max);
      });
      setAttr(patch);
    },
    [setAttr, cornerValue, attrs]
  );

  // Manual numeric input for the (shared) corner radius. Clamps to range.
  const setCornerValue = useCallback(
    (v) => {
      const clamped = Math.max(0, Math.min(v, cornerMax));
      setAttr({ cornerRadius: clamped });
    },
    [setAttr, cornerMax]
  );

  // Toggle independent per-corner mode. When turning it on, seed each corner
  // with the current shared value so the shape doesn't suddenly change.
  const toggleIndividual = useCallback(() => {
    const next = !individual;
    if (next) {
      const patch = { cornerIndividual: true };
      CORNER_KEYS.forEach((k) => { patch[k] = cornerValue; });
      setAttr(patch);
    } else {
      // Turning off: fold the four corners back into the shared value (use TL
      // as the representative value, like Figma does when you collapse).
      setAttr({ cornerIndividual: false, cornerRadius: Number(attrs.cornerTL) || 0 });
    }
  }, [individual, cornerValue, attrs, setAttr]);

  // Set a single corner's radius (independent mode).
  const setSingleCorner = useCallback(
    (key, v) => {
      const clamped = Math.max(0, Math.min(v, cornerMax));
      setAttr({ [key]: clamped });
    },
    [setAttr, cornerMax]
  );

  return (
    <PanelSection title="Corner Radius · 圆角">
      <Field label={`Radius · ${individual ? "独立" : `${cornerValue}${cornerUnit}`}`}>
        <div className="flex items-center gap-2">
          <div className="flex-1 pt-1">
            <Slider
              value={[Math.min(cornerValue, cornerMax)]}
              min={0}
              max={cornerMax}
              step={1}
              onValueChange={([v]) => setCornerValue(v)}
              disabled={individual}
              data-testid={`shape-corner-radius-slider`}
            />
          </div>
          <div className="w-16 shrink-0">
            <NumberInput
              value={cornerValue}
              onChange={(v) => setCornerValue(v)}
              disabled={individual}
              testId="shape-corner-radius-input"
            />
          </div>
          <div className="flex border border-line bg-chrome shrink-0">
            <button
              type="button"
              onClick={() => setCornerUnit("px")}
              data-testid="shape-corner-unit-px"
              className={`px-2 h-8 text-[10px] font-mono uppercase ${cornerUnit === "px" ? "bg-[#002FA7] text-white" : "text-ink3 hover:text-ink"}`}
            >
              px
            </button>
            <button
              type="button"
              onClick={() => setCornerUnit("%")}
              data-testid="shape-corner-unit-pct"
              className={`px-2 h-8 text-[10px] font-mono uppercase border-l border-line ${cornerUnit === "%" ? "bg-[#002FA7] text-white" : "text-ink3 hover:text-ink"}`}
            >
              %
            </button>
          </div>
        </div>
        {/* Independent per-corner toggle (rect / image only). */}
        {type !== "polygon" && (
          <button
            type="button"
            onClick={toggleIndividual}
            data-testid="shape-corner-individual-toggle"
            className={`mt-2 flex items-center gap-1.5 px-2 h-7 text-[10px] font-mono uppercase border border-line ${individual ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-chrome text-ink3 hover:text-ink"}`}
          >
            <CornerDownRight className="h-3 w-3" />
            独立圆角 · Independent
          </button>
        )}
        {/* Per-corner inputs (only shown in independent mode). */}
        {individual && (
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="shape-corner-individual-grid">
            {CORNER_KEYS.map((k) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-ink4 w-8 shrink-0">{CORNER_LABELS[k]}</span>
                <div className="flex-1">
                  <NumberInput
                    value={Number(attrs[k]) || 0}
                    onChange={(v) => setSingleCorner(k, v)}
                    testId={`shape-corner-${k}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Field>
    </PanelSection>
  );
}
